import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import {
  GameInfo,
  CardGradeQueryResponse,
  CardGradeViewQueryResponse,
  GradeNameDto,
  PublicCountsDto,
} from "types/gameInfo";

import { filterCardsGraded } from "constants/grades";

import { findDuplicateNames, isMintGameCode, nameKey, resolveGameCategory } from "lib";
import type { HomeCategory } from "lib";

import { useGetGamesQuery } from "./pcgPublicV2";
import { GameSummaryDto } from "types/v2/catalog";

/*
  D12's verdict enum (spec §14/D12). Added here by P3 as the type only — P2 adds
  `VerifyResponse`, `NfcTapState`, the `verifyNfcTap` mutation and
  `useVerifyNfcTapMutation` beside it (R2, implementer-contract.md). Every NFC type
  is defined once, in this file.
*/
export type NfcVerdict =
  | "UNKNOWN_CHIP"
  | "INVALID"
  | "KEY_RETIRED"
  | "SUPERSEDED"
  | "REPLAYED"
  | "VERIFIED";

/*
  POST /public/api/v1/nfc/verify's answer (P2). `gradedId` is a string, omitted when the
  chip is not bound to a slab (rows 1–2 of D12: UNKNOWN_CHIP, INVALID).
*/
export interface VerifyResponse {
  verdict: NfcVerdict;
  gradedId?: string;
}

/*
  The router state /t hands to /report/:gradedId (REQ-045, D14b). Router state only —
  never a path segment, query parameter or web storage (global-constraints.md).
*/
export interface NfcTapState {
  nfcVerdict: NfcVerdict;
}

// Optional REACT_APP_API_SERVER override, e.g. http://localhost:8080 for local
// development. Unset (the deployed default) means production. See the README.
const API_SERVER = process.env.REACT_APP_API_SERVER || "https://app.premiercardgrading.com";

export const pcgPublicApi = createApi({
  reducerPath: "pcgPublicApi",
  baseQuery: fetchBaseQuery({ baseUrl: `${API_SERVER}/public/api/` }),
  endpoints: (builder) => ({
    getGameInfo: builder.query<GameInfo[], void>({
      query: () => `v1/gameInfo?includeCounts=true`,
    }),
    getCardGrade: builder.query<
      CardGradeQueryResponse,
      { gradedId: string | undefined }
    >({
      query: ({ gradedId }) => `v1/gradings?gradedId=${gradedId}`,
    }),
    getGradeNames: builder.query<GradeNameDto[], void>({
      query: () => `v2/gradeNames`,
    }),

    /**
     * The home statistics band's five figures (spec hero-stats-band.md §6.1, §6.8).
     * GET /public/api/v1/counts — public, unauthenticated, global, no parameters.
     *
     * `keepUnusedDataFor` is set HERE rather than on the slice: pcgPublicApi declares no
     * slice-wide value, so it would otherwise inherit RTK's 60s default and re-fetch on
     * every return to `/`. 1800 matches the v2 catalog slice. Scoped to this endpoint so
     * the other v1 queries keep the caching they were written against.
     */
    getCounts: builder.query<PublicCountsDto, void>({
      query: () => `v1/counts`,
      keepUnusedDataFor: 1800,
    }),

    /*
      The home Population Report card's "Just certified" ticker: the eight most recently
      COMPLETED rows of the public grading feed, the same call the events storefront's live
      stats panel makes. `sort=-completedOn` is the only sort the public feed honours
      (backend !206); without it the feed is newest submitted, mostly cards still in grading.
      Raw rows; selectJustCertified (lib/heroStats.ts) keeps the certified ones.
    */
    getRecentGradings: builder.query<CardGradeQueryResponse, void>({
      query: () => `v1/gradings?limit=8&sort=-completedOn`,
    }),

    /*
      The header stats ticker's "Hot right now": the 200 newest completions (the feed's cap),
      grouped by set in selectHotSet (lib/headerStats.ts). About 12 KB gzipped.
    */
    getHotGradings: builder.query<CardGradeQueryResponse, void>({
      query: () => `v1/gradings?limit=200&sort=-completedOn`,
    }),

    /*
      The header stats ticker's "Latest Flawless 10": the feed filtered by grade name, newest
      completion first. `grade=` matches the grade name exactly (Flawless, Pristine, …).
    */
    getLatestFlawless: builder.query<CardGradeQueryResponse, void>({
      query: () => `v1/gradings?limit=4&sort=-completedOn&grade=Flawless`,
    }),

    /*
      The /t tap route's one call (D3, REQ-044). A mutation, not a query: every tap is a
      fresh verification with a server-side effect, so nothing is cached or shared
      between calls.
    */
    verifyNfcTap: builder.mutation<VerifyResponse, { u: string; c: string; m: string }>({
      query: (body) => ({ url: `v1/nfc/verify`, method: "POST", body }),
    }),
  }),
});

export const {
  useGetGameInfoQuery,
  useGetCardGradeQuery,
  useGetGradeNamesQuery,
  useGetCountsQuery,
  useGetRecentGradingsQuery,
  useGetHotGradingsQuery,
  useGetLatestFlawlessQuery,
  useVerifyNfcTapMutation,
  usePrefetch,
} = pcgPublicApi;

/*
  The AppBar's nav menu entries: name, code, and whether the name is shared with another
  game so the menu can tell the two apart.
*/
export interface GameMenuOption {
  id: number;
  name: string;
  gameCode: string;
  duplicateName: boolean;
  /* Which side of the menu's TCG | Sports toggle it lists under (resolveGameCategory). */
  category: HomeCategory;
}

/*
  The v2 games shaped for the AppBar's nav menu, with New Zealand Mint dropped (D16, §5.2).

  v2 rather than v1 /gameInfo, which only lists reviewed games: with scope=population the
  menu reaches every game the home page does. Deduped by id, then sorted A to Z by name.
  Archived games are left out: the menu is the live catalogue, and the home page's Archive
  section is where those are reached, with the context that they are archived.
  Exported and pure so the D16 rule is unit-testable at the place the spec puts it.

  The order used to be whatever the API sent, which put the menu in no order a visitor
  could scan (D18 of docs/specs/pop-language-split.md, Robert, 2026-10-20). Sensitivity
  `base`, so "Pokémon" files with the Ps and case never splits a letter. The sort is
  stable, so same-named games keep the API's relative order. The AppBar is the only
  consumer, so the order is the menu's to own here.
*/
export const selectGameOptions = (
  rows: GameSummaryDto[] | undefined
): GameMenuOption[] => {
  const seen = new Set<number>();
  const games = (rows ?? []).filter((game) => {
    if (isMintGameCode(game.gameCode) || game.archived === true || seen.has(game.id)) {
      return false;
    }
    /* Neither TCG nor Sports (Coins): on neither side of the toggle, so not in the menu. */
    if (resolveGameCategory(game.category) === null) {
      return false;
    }
    seen.add(game.id);
    return true;
  });
  /* Trimmed: the catalog has " Build Divide", leading space and all, which sorted first. */
  games.sort((a, b) => a.name.trim().localeCompare(b.name.trim(), undefined, { sensitivity: "base" }));
  const duplicates = findDuplicateNames(games);
  return games.map((game) => ({
    id: game.id,
    name: game.name,
    gameCode: game.gameCode,
    duplicateName: duplicates.has(nameKey(game.name)),
    category: resolveGameCategory(game.category) as HomeCategory,
  }));
};

export interface GameOptionsResult {
  options: GameMenuOption[];
  isLoading: boolean;
  isError: boolean;
}

/*
  This used to return a bare [] for loading AND for failure, which made a distinct menu
  loading state and a "games unavailable" state impossible to express against it (§6.2).
  The three facts are reported separately now, and the AppBar renders one state per fact.
*/
export const useGetGameOptions = (): GameOptionsResult => {
  const { data, error, isLoading } = useGetGamesQuery();
  return {
    options: !isLoading && !error ? selectGameOptions(data) : [],
    isLoading,
    isError: !!error,
  };
};

export interface SearchOptions {
  options: {
    [key: string]: {
      sets: string[];
      finish: string[];
    };
  };
  gradeNames: string[];
}

/*
  The v1 rows shaped for the advanced-search dropdowns, with New Zealand Mint dropped
  (D16, §5.2).

  The exclusion has to happen HERE, on the raw rows, and not in SearchCardsAdvanced: the
  reduce below keys by game *name* and never carries `gameCode` out, so by the time the
  component sees `options` there is nothing left to match on. Same shape as the sibling
  selectGameOptions above — exported and pure so the rule is unit-tested where §5.2 puts
  it, since component tests cannot run here (§2).
*/
/*
  One option per set name, first occurrence winning (D29).

  A game's set list really does carry the same setName more than once: of the 42
  duplicate-name groups visible in production, 25 are genuinely different languages
  (English/Japanese/Korean/Chinese), 15 differ only because one copy has no `language`
  recorded, and 2 are catalog duplicates proper. So the duplication is mostly REAL —
  but it is not reachable from here. `sets` is a string[] of names, and every query
  downstream keys on the name (`v1/gradings?...setName=`, `v1/gameInfo/{code}/set/{name}`),
  never on the set id. Both rows therefore return the same merged population, which makes
  collapsing them lossless and makes labelling one "(Japanese)" actively misleading — the
  search cannot honour it. Splitting by language needs the id plumbed through the query,
  which is a backend change and out of scope (D11).
*/
const dedupeSetNames = (setNames: string[]): string[] => {
  const seen = new Set<string>();
  return setNames.filter((setName) => {
    /*
      Compared trimmed, kept as it arrived. Production really does carry both "S&V: 151"
      and "S&V: 151 " — different rows, different languages, one trailing space — and an
      exact-string compare leaves the visitor two options that look identical. The value
      emitted stays the first row's own string, since that is what the search sends.
    */
    const key = setName.trim();
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export const selectSearchOptions = (
  rows: GameInfo[] | undefined,
  gradeNames: GradeNameDto[] | undefined
): SearchOptions => ({
  options: (rows ?? [])
    .filter((row) => !isMintGameCode(row.gameCode))
    .reduce((acc: SearchOptions["options"], row) => {
      acc[row.game] = {
        sets: dedupeSetNames(
          row.sets.filter(filterCardsGraded(null)).map((set) => set.setName)
        ),
        finish: row.finish.map((finish) => finish.printing),
      };
      return acc;
    }, {}),
  // Previously used names where there is one, for backward compatibility.
  gradeNames: (gradeNames ?? []).map((dto) => dto.previousGradeName || dto.gradeName),
});

export const useGetSearchOptions = (): SearchOptions => {
  const { data, error, isLoading } = useGetGameInfoQuery();
  const {
    data: gradeNamesData,
    error: gradeNamesError,
    isLoading: gradeNamesIsLoading,
  } = useGetGradeNamesQuery();

  if (
    !isLoading &&
    !gradeNamesIsLoading &&
    !error &&
    !gradeNamesError &&
    data &&
    gradeNamesData
  ) {
    return selectSearchOptions(data, gradeNamesData);
  }

  /* Loading and failure both return safe empties — task 12 relies on this. */
  return {
    options: {},
    gradeNames: [],
  };
};

export const useGetGradeReportViewQuery = ({
  gradedId,
}: {
  gradedId: string;
}): CardGradeViewQueryResponse => {
  const { data, error, isLoading, isFetching } = useGetCardGradeQuery({
    gradedId,
  });
  if (!isLoading && !error && data) {
    const { totalElements, totalPages, data: resultsData } = data;
    if (resultsData && resultsData.length > 0) {
      return {
        totalElements,
        totalPages,
        data: resultsData[0] || null,
        error,
        isLoading,
        isFetching,
        cardInfo: resultsData[0].cardInfo,
      };
    }
  }
  return {
    totalElements: 0,
    totalPages: 0,
    data: null,
    error,
    isLoading,
    isFetching,
    cardInfo: null,
  };
};

